import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  LogOut,
  DollarSign,
  Grid,
  List,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import './POS.css';

const POS = () => {
  const { user, logout } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [customAmount, setCustomAmount] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/products`, { headers });
      // Filter only POS-available products
      const posProducts = response.data.filter(p => p.available_for_pos);
      setProducts(posProducts);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.price }
          : item
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        price: product.price,
        quantity: 1,
        total: product.price
      }]);
    }
    
    toast.success(`Added ${product.name} to cart`);
  };

  const updateQuantity = (productId, change) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = Math.max(0, item.quantity + change);
        return newQuantity === 0 
          ? null 
          : { ...item, quantity: newQuantity, total: newQuantity * item.price };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const addCustomAmount = () => {
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    const customItem = {
      product_id: `custom-${Date.now()}`,
      product_name: 'Custom Amount',
      price: amount,
      quantity: 1,
      total: amount
    };

    setCart([...cart, customItem]);
    setCustomAmount('');
    toast.success('Custom amount added');
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.total, 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    const total = calculateTotal();
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const origin = window.location.origin;
      
      const response = await axios.post(
        `${API}/pos/create-invoice`,
        {
          amount: total,
          currency: 'usd',
          success_url: `${origin}/pos/receipt/{{CHECKOUT_SESSION_ID}}`,
          cancel_url: `${origin}/pos`,
          metadata: {
            items: cart.map(item => ({
              name: item.product_name,
              quantity: item.quantity,
              price: item.price
            }))
          }
        },
        { headers }
      );

      // Navigate to payment screen
      navigate(`/pos/payment/${response.data.invoice_id}`, {
        state: { 
          invoiceData: response.data,
          cart: cart,
          total: total
        }
      });
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create payment';
      toast.error(errorMsg);
    }
  };

  const clearCart = () => {
    if (cart.length === 0) return;
    if (window.confirm('Clear all items from cart?')) {
      setCart([]);
      toast.success('Cart cleared');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="pos-page">
      <header className="pos-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={28} />
              <span>POS System</span>
            </div>
            <span className="cashier-badge">
              {user?.role === 'cashier' ? 'Cashier' : 'Owner'}: {user?.business_name}
            </span>
          </div>
          <div className="header-right">
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/dashboard')}>
              Dashboard
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleLogout}>
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="pos-container">
        {/* Products Section */}
        <div className="pos-products">
          <div className="products-header">
            <input
              type="text"
              className="search-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="view-toggle">
              <button 
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
              >
                <Grid size={18} />
              </button>
              <button 
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
              >
                <List size={18} />
              </button>
            </div>
          </div>

          {/* Custom Amount */}
          <div className="custom-amount-section">
            <input
              type="number"
              step="0.01"
              placeholder="Enter custom amount"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCustomAmount()}
            />
            <button className="btn btn-primary btn-sm" onClick={addCustomAmount}>
              <Plus size={16} /> Add
            </button>
          </div>

          {/* Products Grid/List */}
          <div className={`products-display ${viewMode}`}>
            {filteredProducts.length === 0 ? (
              <div className="empty-products">
                <p>No products found</p>
              </div>
            ) : (
              filteredProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="product-item"
                  onClick={() => addToCart(product)}
                >
                  {product.image_url && viewMode === 'grid' && (
                    <div 
                      className="product-image" 
                      style={{backgroundImage: `url(${product.image_url})`}} 
                    />
                  )}
                  <div className="product-details">
                    <h3 className="product-name">{product.name}</h3>
                    {product.description && viewMode === 'list' && (
                      <p className="product-desc">{product.description}</p>
                    )}
                    <div className="product-price">${product.price.toFixed(2)}</div>
                  </div>
                  <button className="add-btn">
                    <Plus size={20} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Cart Section */}
        <div className="pos-cart">
          <div className="cart-header">
            <h2>
              <ShoppingCart size={24} />
              Current Order
            </h2>
            {cart.length > 0 && (
              <button className="btn-text" onClick={clearCart}>
                Clear All
              </button>
            )}
          </div>

          <div className="cart-items">
            {cart.length === 0 ? (
              <div className="empty-cart">
                <ShoppingCart size={48} color="rgba(228, 230, 235, 0.3)" />
                <p>Cart is empty</p>
                <small>Add items to start</small>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.product_id} className="cart-item">
                  <div className="cart-item-info">
                    <h4>{item.product_name}</h4>
                    <p className="item-price">${item.price.toFixed(2)} each</p>
                  </div>
                  <div className="cart-item-controls">
                    <div className="quantity-controls">
                      <button onClick={() => updateQuantity(item.product_id, -1)}>
                        <Minus size={16} />
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, 1)}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="item-total">${item.total.toFixed(2)}</div>
                    <button 
                      className="btn-icon-small danger"
                      onClick={() => removeFromCart(item.product_id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="cart-footer">
            <div className="cart-summary">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
            
            <button 
              className="btn btn-primary btn-large btn-checkout"
              onClick={handleCheckout}
              disabled={cart.length === 0}
            >
              <CreditCard size={20} />
              Charge ${calculateTotal().toFixed(2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POS;
